<template>
  <section
    class="decision-analytics-panel mt-4 mb-5"
    data-testid="overview-decision-analytics-panel"
  >
    <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
      <h2 class="text-[13px] uppercase tracking-wider text-[#ABABAB] font-medium m-0">
        {{ $t('UserProjects.decisions.analytics.title') }}
      </h2>
      <span
        v-if="data?.formulaVersion"
        class="text-[10px] text-[var(--color-disabled)]"
      >
        {{ data.formulaVersion }}
      </span>
    </div>

    <Message v-if="error" severity="error" :closable="false" class="mb-3">
      {{ error }}
    </Message>

    <div v-if="loading" class="text-sm text-[var(--color-disabled)] py-4">
      {{ $t('UserProjects.decisions.analytics.loading') }}
    </div>

    <template v-else-if="data">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <article
          v-for="tile in tiles"
          :key="tile.id"
          class="rounded-[10px] border border-[#E5E5E5] bg-white p-3"
          :data-testid="`decision-analytics-tile-${tile.id}`"
        >
          <p class="text-[11px] text-[var(--color-disabled)] m-0 mb-1">{{ tile.label }}</p>
          <p class="text-[20px] font-bold text-[var(--color-dark)] m-0 leading-tight">
            {{ tile.value }}
          </p>
          <p class="text-[11px] text-[#ABABAB] m-0 mt-1">{{ tile.hint }}</p>
        </article>
      </div>

      <div class="rounded-[10px] border border-[#E5E5E5] bg-white p-3 mb-4">
        <p class="text-[11px] uppercase tracking-wider text-[#ABABAB] font-medium m-0 mb-2">
          {{ $t('UserProjects.decisions.analytics.escalationDistribution') }}
        </p>
        <div class="grid grid-cols-4 gap-2" data-testid="decision-analytics-escalation-dist">
          <div
            v-for="level in escalationLevels"
            :key="level.key"
            class="text-center rounded-[8px] bg-[#F8F8F8] p-2"
          >
            <p class="text-[10px] text-[var(--color-disabled)] m-0">{{ level.label }}</p>
            <p class="text-[16px] font-bold text-[var(--color-dark)] m-0">{{ level.count }}</p>
          </div>
        </div>
      </div>

      <div class="rounded-[10px] border border-[#E5E5E5] bg-white p-3">
        <p class="text-[11px] uppercase tracking-wider text-[#ABABAB] font-medium m-0 mb-2">
          {{ $t('UserProjects.decisions.analytics.openTrend') }}
        </p>
        <div
          v-if="!trendRows.length"
          class="text-sm text-[var(--color-disabled)] py-3 text-center"
        >
          {{ $t('UserProjects.decisions.analytics.trendEmpty') }}
        </div>
        <div v-else class="overflow-x-auto" data-testid="decision-analytics-trend">
          <table class="w-full text-left text-[12px]">
            <thead>
              <tr class="text-[var(--color-disabled)]">
                <th class="font-medium py-1 pr-2">{{ $t('UserProjects.decisions.analytics.week') }}</th>
                <th class="font-medium py-1 pr-2">{{ $t('UserProjects.decisions.analytics.created') }}</th>
                <th class="font-medium py-1 pr-2">{{ $t('UserProjects.decisions.analytics.decided') }}</th>
                <th class="font-medium py-1">{{ $t('UserProjects.decisions.analytics.escalated') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in trendRows" :key="row.periodKey" class="border-t border-[#F0F0F0]">
                <td class="py-1.5 pr-2 text-[var(--color-dark)]">{{ row.label }}</td>
                <td class="py-1.5 pr-2">{{ row.created }}</td>
                <td class="py-1.5 pr-2">{{ row.decided }}</td>
                <td class="py-1.5">{{ row.escalated }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import Message from 'primevue/message';

export default {
  name: 'OverviewDecisionAnalyticsPanel',
  components: { Message },
  props: {
    data: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    error: { type: String, default: null },
  },
  computed: {
    tiles() {
      const d = this.data || {};
      return [
        {
          id: 'avg-speed',
          label: this.$t('UserProjects.decisions.analytics.avgSpeed'),
          value: d.avgTimeToDecideDays == null
            ? '—'
            : `${d.avgTimeToDecideDays} ${this.$t('UserProjects.decisions.analytics.days')}`,
          hint: this.$t('UserProjects.decisions.analytics.avgSpeedHint'),
        },
        {
          id: 'overdue',
          label: this.$t('UserProjects.decisions.analytics.overdue'),
          value: String(d.overdueCount ?? 0),
          hint: this.$t('UserProjects.decisions.analytics.overdueHint'),
        },
        {
          id: 'escalated',
          label: this.$t('UserProjects.decisions.analytics.escalatedPending'),
          value: String(d.escalatedCount ?? 0),
          hint: this.$t('UserProjects.decisions.analytics.escalatedHint'),
        },
        {
          id: 'pending',
          label: this.$t('UserProjects.decisions.analytics.pending'),
          value: String(d.pendingCount ?? 0),
          hint: this.$t('UserProjects.decisions.analytics.pendingHint'),
        },
      ];
    },
    escalationLevels() {
      const dist = this.data?.escalationDistribution || {};
      return [0, 1, 2, 3].map((level) => ({
        key: `level${level}`,
        label: this.$t('UserProjects.decisions.analytics.level', { level }),
        count: dist[`level${level}`] ?? 0,
      }));
    },
    trendRows() {
      return this.data?.openTrendByWeek || [];
    },
  },
};
</script>
